import {
  FILE_TABLE_ACTIONS,
  FILE_TABLE_ACTION_DELETE,
  FILE_TABLE_ACTION_DOWNLOAD,
  FILE_TABLE_ACTION_SEE_DETAILS,
  FILE_TABLE_ACTION_SHARE,
  FILE_TABLE_ACTION_SYNC_VERSION,
  FILE_TABLE_ACTION_SYNC_VERSION_HIS,
  SEALD_REDACTED,
  TRACK_EVENTS,
} from "core/consts";
import { mimeTypeToExtensionMap } from "core/model/files/config";
import {
  FileHISStateful,
  FileState,
  FileTable,
  FileTableActions,
  Filev2Stateful,
  TrackEventFn,
} from "core/types";
import {
  ActionMenuOption,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import {
  isFileForm,
  isFileFormUploaded,
} from "dsl/ecosystems/PatientFiles/utils";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import {
  DownloadIcon,
  RefreshCwIcon,
  SearchIcon,
  SendIcon,
  Trash2Icon,
} from "lucide-react";
import Translations from "translations/types";

export const getFileListActionOptions = ({
  canAccessPatientData,
  file,
  fileTable,
  isExtractActive,
  isFileDeleted,
  isNewVersionAvailable,
  setAction,
  trackEvent,
  translations,
}: {
  canAccessPatientData: boolean;
  file: FileState;
  fileTable: FileTable;
  isNewVersionAvailable?: boolean;
  isFileDeleted?: boolean;
  setAction: SetAction<FileFeatureData>;
  trackEvent: TrackEventFn;
  translations: Translations;
  isExtractActive?: boolean;
}): ActionMenuOption<FileTableActions>[] => {
  const isRecareFileFormUploaded = isFileFormUploaded(file);

  if (isFileHISStateful(file) || isFileForm(file)) {
    return [
      ...(isExtractActive
        ? []
        : ([
            {
              color: "primary",
              id: FILE_TABLE_ACTION_SHARE,
              label: translations.actions.shareFile,
              LeftIcon: SendIcon,
              loading: "na",
              onClick: () => {
                trackEvent({
                  file_implementation: "HIS",
                  name: TRACK_EVENTS.FILE_MENU_MAIN_SHARE_BUTTON_CLICKED,
                  section: fileTable,
                });
                setAction({ data: file, type: FILE_TABLE_ACTION_SHARE });
              },
              size: "small",
              type: "button",
              variant: "outlined",
            },
          ] as ActionMenuOption<FileTableActions>[])),
      {
        disabled: !canAccessPatientData,
        icon: <DownloadIcon size={14} />,
        id: FILE_TABLE_ACTION_DOWNLOAD,
        label: translations.actions.download,
        onClick: () => {
          trackEvent({
            file_implementation: "HIS",
            item: "download",
            name: TRACK_EVENTS.FILE_MENU_ITEM_CLICKED,
            section: fileTable,
          });
          setAction({ data: file, type: FILE_TABLE_ACTION_DOWNLOAD });
        },
      },
      {
        disabled: true,
        icon: <Trash2Icon size={14} />,
        id: FILE_TABLE_ACTION_DELETE,
        label: translations.actions.delete,
        onClick: () => {
          trackEvent({
            file_implementation: "HIS",
            item: "delete",
            name: TRACK_EVENTS.FILE_MENU_ITEM_CLICKED,
            section: fileTable,
          });
          setAction({ data: file, type: FILE_TABLE_ACTION_DELETE });
        },
        tooltip: isFileHISStateful(file)
          ? translations.fileSection.fileListComponent.validation
              .tooltipHisFileNoDeletion
          : translations.fileSection.fileListComponent.validation
              .tooltipDeleteRecare,
      },
      {
        icon: <SearchIcon size={14} />,
        id: FILE_TABLE_ACTION_SEE_DETAILS,
        label: translations.actions.seeDetails,
        onClick: () => {
          trackEvent({
            file_implementation: "HIS",
            item: "detailed_overview",
            name: TRACK_EVENTS.FILE_MENU_ITEM_CLICKED,
            section: fileTable,
          });
          setAction({
            data: file,
            type: isExtractActive
              ? FILE_TABLE_ACTION_SEE_DETAILS
              : FILE_TABLE_ACTION_SHARE,
          });
        },
      },
    ];
  }

  const actions: ActionMenuOption<FileTableActions>[] = [];

  if (isNewVersionAvailable) {
    actions.push({
      color: "primary",
      id: isRecareFileFormUploaded
        ? FILE_TABLE_ACTION_SYNC_VERSION
        : FILE_TABLE_ACTION_SYNC_VERSION_HIS,
      label: translations.fileSection.fileListComponent.reloadButton,
      LeftIcon: RefreshCwIcon,
      loading: "na",
      onClick: () => {
        trackEvent({
          file_implementation: "HIS",
          name: TRACK_EVENTS.FILE_MENU_MAIN_RELOAD_BUTTON_CLICKED,
          section: fileTable,
        });
        setAction({
          data: isRecareFileFormUploaded
            ? file
            : [
                {
                  document_category_number: file.category,
                  document_file_name: `${file.file_name?.decrypted}${
                    mimeTypeToExtensionMap[
                      file.file_type as keyof typeof mimeTypeToExtensionMap
                    ]
                  }`,
                  document_id: file.kis_document_id_string ?? "",
                  fileV2Id: file.id,
                  id: file.kis_document_id_string ?? "",
                  share_mode: file.share_mode,
                } as FileHISStateful & { fileV2Id?: Filev2Stateful["id"] },
              ],
          type: isRecareFileFormUploaded
            ? FILE_TABLE_ACTION_SYNC_VERSION
            : FILE_TABLE_ACTION_SYNC_VERSION_HIS,
        });
      },
      size: "small",
      type: "button",
      variant: "outlined",
    });
  } else if (isFileDeleted) {
    actions.push({
      color: "primary",
      id: FILE_TABLE_ACTION_DELETE,
      label: translations.fileSection.fileListComponent.buttonDelete,
      LeftIcon: Trash2Icon,
      loading: "na",
      onClick: () => {
        trackEvent({
          name: TRACK_EVENTS.FILE_MENU_MAIN_DELETE_BUTTON_CLICKED,
          section: fileTable,
        });
        setAction({
          data: file,
          type: FILE_TABLE_ACTION_DELETE,
        });
      },
      size: "small",
      type: "button",
      variant: "outlined",
    });
  } else if (!isExtractActive) {
    actions.push({
      color: "primary",
      id: FILE_TABLE_ACTION_SHARE,
      label: translations.actions.shareFile,
      LeftIcon: SendIcon,
      loading: "na",
      onClick: () => {
        trackEvent({
          file_implementation: "HIS",
          name: TRACK_EVENTS.FILE_MENU_MAIN_SHARE_BUTTON_CLICKED,
          section: fileTable,
        });
        setAction({ data: file, type: FILE_TABLE_ACTION_SHARE });
      },
      size: "small",
      type: "button",
      variant: "outlined",
    });
  }

  return [
    ...actions,
    {
      disabled:
        file.file_name?.seald_content === SEALD_REDACTED ||
        !canAccessPatientData,
      icon: <DownloadIcon size={14} />,
      id: FILE_TABLE_ACTION_DOWNLOAD,
      label: translations.actions.download,
      onClick: () => {
        trackEvent({
          file_implementation: "HIS",
          item: "download",
          name: TRACK_EVENTS.FILE_MENU_ITEM_CLICKED,
          section: fileTable,
        });
        setAction({ data: file, type: FILE_TABLE_ACTION_DOWNLOAD });
      },
    },
    {
      icon: <Trash2Icon size={FONT_SIZE_14} />,
      id: FILE_TABLE_ACTION_DELETE,
      label: translations.actions.delete,
      onClick: () => {
        trackEvent({
          file_implementation: "HIS",
          item: "delete",
          name: TRACK_EVENTS.FILE_MENU_ITEM_CLICKED,
          section: fileTable,
        });
        setAction({ data: file, type: FILE_TABLE_ACTION_DELETE });
      },
    },
    {
      icon: <SearchIcon size={14} />,
      id: FILE_TABLE_ACTION_SEE_DETAILS,
      label: translations.actions.seeDetails,
      onClick: () => {
        trackEvent({
          file_implementation: "HIS",
          item: "detailed_overview",
          name: TRACK_EVENTS.FILE_MENU_ITEM_CLICKED,
          section: fileTable,
        });
        setAction({
          data: file,
          type: isExtractActive
            ? FILE_TABLE_ACTION_SEE_DETAILS
            : FILE_TABLE_ACTION_SHARE,
        });
      },
    },
  ];
};

export function getAllowList({
  fileTable,
  isDocsAndExtractActive,
}: {
  fileTable: FileTable;
  isDocsAndExtractActive?: boolean;
}): FileTableActions {
  switch (fileTable) {
    case "sent": {
      return [FILE_TABLE_ACTION_DOWNLOAD, FILE_TABLE_ACTION_DELETE];
    }
    case "available":
    case "uploaded": {
      return FILE_TABLE_ACTIONS.filter((action) =>
        isDocsAndExtractActive ? action !== FILE_TABLE_ACTION_SHARE : true,
      );
    }
    case "received":
      return [FILE_TABLE_ACTION_DOWNLOAD];

    default:
      return [];
  }
}
